import { useMemo, useState } from "react";

import { Column, ErrorText, LoadingIndicator, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGetJobsByCompanyId } from "@/hooks/queries/useGetJobsByCompanyId";
import { useGetLinkedJobs } from "@/hooks/queries/useGetLinkedJobs";
import { jobStatusToLabel } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Job } from "@/types";

// Pick a job to link as a "version of the same job". Same-company only,
// and CLOSED jobs are deliberately included — the primary link target
// is usually a closed job (e.g. the previous hiring day's posting).
export const JobLinkPickerDialog = ({
  isError,
  isLoading,
  job,
  onConfirm,
  onOpenChange,
  open,
}: {
  isError?: boolean;
  isLoading?: boolean;
  job: Job;
  onConfirm: (targetJobId: string) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: companyJobs, isPending: isCompanyJobsPending } =
    useGetJobsByCompanyId();
  const { data: linkedJobs, isPending: isLinkedJobsPending } = useGetLinkedJobs(
    job.id,
  );

  /* React Hooks */
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const linkedIds = new Set((linkedJobs ?? []).map((linked) => linked.id));
    return (companyJobs ?? [])
      .map((companyJob) => companyJob.job)
      .filter((candidate) => candidate.id !== job.id)
      .filter((candidate) => !linkedIds.has(candidate.id));
  }, [companyJobs, job.id, linkedJobs]);

  /* Handlers */
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedJobId(null);
    }
    onOpenChange(isOpen);
  };

  const handleConfirm = () => {
    if (!selectedJobId || isLoading) {
      return;
    }
    onConfirm(selectedJobId);
  };

  /* Render */
  const renderCandidates = () => {
    if (isCompanyJobsPending || isLinkedJobsPending) {
      return (
        <Row className="justify-center py-4">
          <LoadingIndicator />
        </Row>
      );
    }
    if (candidates.length === 0) {
      return (
        <Text className="py-4 text-center text-slate-500">
          No other jobs found for this company.
        </Text>
      );
    }
    return (
      <Column className="max-h-72 gap-1 overflow-y-auto">
        {candidates.map((candidate) => (
          <Button
            key={candidate.id}
            className={cn(
              "h-auto justify-between rounded-md px-3 py-2",
              selectedJobId === candidate.id && "bg-primary-200",
            )}
            onClick={() => {
              setSelectedJobId(candidate.id);
            }}
            variant="ghost"
          >
            <Text className="truncate">{candidate.title}</Text>
            <Text className="shrink-0 text-slate-500">
              {jobStatusToLabel[candidate.status]}
            </Text>
          </Button>
        ))}
      </Column>
    );
  };

  const render = () => (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className="flex flex-col gap-6">
        <DialogHeader className="flex flex-col gap-4">
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Link a job
          </DialogTitle>
          <DialogDescription className="!m-0 text-base font-medium text-slate-700">
            Linked jobs are versions of the same job. For example, the same role
            reopened for a later hiring day. Closed jobs can be linked.
          </DialogDescription>
        </DialogHeader>
        {renderCandidates()}
        <Row className="justify-end gap-2">
          <Button
            className="rounded-full"
            disabled={isLoading}
            onClick={() => {
              handleOpenChange(false);
            }}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="w-24 rounded-full"
            disabled={!!isLoading || !selectedJobId}
            onClick={handleConfirm}
          >
            {isLoading ? (
              <LoadingIndicator gearColor="white" variant="small" />
            ) : (
              <span>Link</span>
            )}
          </Button>
        </Row>
        {isError && (
          <ErrorText errorMessage="Failed to link the jobs. Please try again." />
        )}
      </DialogContent>
    </Dialog>
  );

  return render();
};
